import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import authService from '../services/authService';

// Crear el contexto
const AuthContext = createContext(null);

/**
 * Hook personalizado para usar el contexto de autenticación
 * @returns {Object} - Contexto de autenticación
 * @throws {Error} - Si se usa fuera del AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

/**
 * Proveedor del contexto de autenticación
 * Maneja el estado global de autenticación de la aplicación
 */
const AuthProviderComponent = ({ children }) => {
  // Estados del contexto
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Verificar estado de autenticación inicial
   */
  const checkInitialAuth = useCallback(() => {
    try {
      setError(null);
      const authenticated = authService.isAuthenticated();
      const user = authService.getCurrentUser();
      
      setIsAuthenticated(authenticated);
      setCurrentUser(user);
      
      if (authenticated && !user) {
        authService.logout();
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    } catch (err) {
      console.error('Error verificando autenticación inicial:', err);
      setError('Error al verificar autenticación');
      setIsAuthenticated(false);
      setCurrentUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Inicializar contexto al montar
   */
  useEffect(() => {
    checkInitialAuth();
  }, [checkInitialAuth]);

  /**
   * Función para iniciar sesión
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña del usuario
   * @returns {Promise<boolean>} - True si el login fue exitoso, false si no.
   */
  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authService.login(email, password);

      if (response.success && response.user) {
        setCurrentUser(response.user);
        setIsAuthenticated(true);
        return true;
      } else {
        // El servicio falló pero no lanzó error (ej. credenciales inválidas)
        setError(response.message || 'Credenciales inválidas.');
        setIsAuthenticated(false);
        setCurrentUser(null);
        return false;
      }
    } catch (err) {
      console.error('Error en login del contexto:', err);
      setError(err.message || 'Error al iniciar sesión');
      setIsAuthenticated(false);
      setCurrentUser(null);
      return false; // Devuelve false en caso de error de red o similar
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Función para cerrar sesión
   */
  const logout = useCallback(() => {
    try {
      authService.logout();
      setCurrentUser(null);
      setIsAuthenticated(false);
      setError(null);
    } catch (err) {
      console.error('Error en logout del contexto:', err);
      // Aún así limpiar el estado local
      setCurrentUser(null);
      setIsAuthenticated(false);
      setError('Error al cerrar sesión');
    }
  }, []);

  /**
   * Función para refrescar datos del usuario
   */
  const refreshUser = useCallback(() => {
    try {
      const user = authService.getCurrentUser();
      if (user) {
        setCurrentUser(user);
        setIsAuthenticated(true);
      } else {
        logout();
      }
    } catch (err) {
      console.error('Error refrescando usuario:', err);
      logout();
    }
  }, [logout]);

  /**
   * Función para limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Verificar si el usuario actual es administrador
   */
  const isAdmin = useMemo(() => {
    return currentUser?.role === 'admin';
  }, [currentUser]);

  /**
   * Verificar si el usuario actual es colaborador
   */
  const isCollaborator = useMemo(() => {
    return currentUser?.role === 'colaborador';
  }, [currentUser]);

  /**
   * Obtener permisos del usuario actual
   */
  const userPermissions = useMemo(() => {
    return currentUser?.permissions || [];
  }, [currentUser]);

  // Valor del contexto memoizado
  const contextValue = useMemo(() => ({
    // Estados
    currentUser,
    isAuthenticated,
    loading,
    error,
    
    // Funciones
    login,
    logout,
    refreshUser,
    clearError,
    
    // Utilidades computadas
    isAdmin,
    isCollaborator,
    userPermissions,
    
    // Información del usuario
    userId: currentUser?.id || null,
    userEmail: currentUser?.email || null,
    userName: currentUser?.name || null,
    userRole: currentUser?.role || null,
    userCelular: currentUser?.celular || null
  }), [
    currentUser,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    refreshUser,
    clearError,
    isAdmin,
    isCollaborator,
    userPermissions
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Crear componente memoizado
export const AuthProvider = React.memo(AuthProviderComponent);
AuthProvider.displayName = 'AuthProvider';

// Exportar el contexto para uso avanzado (opcional)
export { AuthContext }; 